<template>
  <div class="owner-program-form">
    <bg-grid>
      <bg-grid-item :col="8">
        <!--
          An ended program is read-only. The fieldset disables every native
          control inside (inputs, selects, buttons, uploaders, option cards);
          the rich text editor isn't a form control, so it takes `disabled`.
        -->
        <fieldset :disabled="isReadOnly" class="owner-program-form__fieldset">
          <program-information
            v-model="programInformation"
            :disabled="isReadOnly"
          />

          <button-settings
            v-model="buttonSettings"
            :disabled="isReadOnly"
            class="bg-u-mt-md"
          />

          <program-target
            v-model="programTarget"
            :disabled="isReadOnly"
            class="bg-u-mt-md"
          />
        </fieldset>
      </bg-grid-item>

      <bg-grid-item :col="4" class="owner-program-form__preview">
        <owner-program-card
          is-preview
          :name="programInformation.name"
          :description="programInformation.description"
          :banner="programInformation.banner"
          :quota="programInformation.quota"
          :start-date="programInformation.startDate"
          :end-date="programInformation.endDate"
          :buttons="previewButtons"
          :text-fields="previewTextFields"
        />
      </bg-grid-item>
    </bg-grid>

    <div v-if="!isReadOnly" class="owner-program-form__actions">
      <template v-if="isEditMode && !isDraftStatus">
        <bg-button
          variant="primary"
          :disabled="!isPublishable"
          :loading="isUpdating"
          @click="handleUpdate"
        >
          Update
        </bg-button>

        <bg-button
          variant="secondary"
          :disabled="isUpdating || !isActiveStatus"
          @click="handleEndProgram"
        >
          Akhiri Program
        </bg-button>
      </template>

      <template v-else>
        <bg-button
          variant="primary"
          :disabled="!isPublishable"
          @click="handlePrimaryAction"
        >
          Publish
        </bg-button>

        <bg-button
          variant="secondary"
          :disabled="!canSaveDraft"
          :loading="isSavingDraft"
          @click="handleDraftAction"
        >
          Simpan Draft
        </bg-button>
      </template>
    </div>

    <publish-confirmation-modal
      v-if="!isEditMode"
      v-model="isPublishModalOpen"
      :program="previewProgram"
      :loading="isPublishing"
      @confirm="handlePublish"
    />
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api';
import { BgGrid, BgGridItem, BgButton } from 'bangul-vue';

import { PROGRAM_TARGET } from '@admin_pages/OwnerProgram/constants';
import ownerProgramApi from '@admin_endpoints/owner-program';
import ProgramInformation from './components/ProgramInformation';
import ButtonSettings from './components/ButtonSettings';
import ProgramTarget from './components/ProgramTarget';
import PublishConfirmationModal from './components/PublishConfirmationModal';

import OwnerProgramCard from '@/components/molecules/OwnerProgramCard';
import { dayjs } from '@/utils/formatter';

import { BUTTON_ACTION } from './constants';
import { buildProgramFormData } from './utils/buildProgramFormData';

const PROGRAM_STATUS = { ACTIVE: 'active', DRAFT: 'draft', PUBLISH: 'publish' };

const SUBMIT_ERROR_MESSAGE = {
  [PROGRAM_STATUS.DRAFT]: 'Gagal menyimpan draft. Silakan coba lagi.',
  [PROGRAM_STATUS.PUBLISH]: 'Gagal mempublish program. Silakan coba lagi.',
};

const UPDATE_ERROR_MESSAGE = 'Gagal memperbarui program. Silakan coba lagi.';
const UPDATE_SUCCESS_MESSAGE = 'Program berhasil diperbarui.';

export default {
  name: 'OwnerProgramForm',

  components: {
    BgGrid,
    BgGridItem,
    BgButton,
    ProgramInformation,
    ButtonSettings,
    ProgramTarget,
    PublishConfirmationModal,
    OwnerProgramCard,
  },

  props: {
    // 'create' renders Publish / Simpan Draft; 'edit' renders Update / Akhiri.
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit'].includes(value),
    },
    // Seeds the form when editing (already mapped via mapProgramDetailToForm).
    initialData: {
      type: Object,
      default: null,
    },
    // The program's current status; only relevant in edit mode (drives footer).
    status: {
      type: String,
      default: null,
    },
    // The program id to update; required in edit mode.
    programId: {
      type: [Number, String],
      default: null,
    },
  },

  setup(props, { emit }) {
    const isEditMode = computed(() => props.mode === 'edit');

    const isDraftStatus = computed(() => props.status === PROGRAM_STATUS.DRAFT);

    const isActiveStatus = computed(
      () => props.status === PROGRAM_STATUS.ACTIVE
    );

    // An ended program can't be edited; the form is shown read-only.
    const isReadOnly = computed(() => props.status === 'ended');

    const initial = props.initialData;
    const initialTargetType = initial?.programTarget?.targetType ?? null;

    const programInformation = ref(
      initial?.programInformation ?? {
        name: '',
        description: '',
        startDate: null,
        endDate: null,
        quota: '',
        banner: null,
      }
    );

    const buttonSettings = ref(
      initial?.buttonSettings ?? {
        ctaType: 'single',
        buttons: [
          { label: '', action: BUTTON_ACTION.SUBMIT, url: '', textFields: [] },
          { label: '', action: BUTTON_ACTION.SUBMIT, url: '', textFields: [] },
        ],
      }
    );

    const programTarget = ref(
      initial?.programTarget ?? {
        targetType: PROGRAM_TARGET.ALL,
        csvFile: {
          file: null,
          url: null,
          isDeleting: false,
          deleted: false,
          error: '',
        },
      }
    );

    const previewButtons = computed(() => {
      const count = buttonSettings.value.ctaType === 'double' ? 2 : 1;

      return buttonSettings.value.buttons.slice(0, count);
    });

    const previewTextFields = computed(() => {
      const count = buttonSettings.value.ctaType === 'double' ? 2 : 1;
      const activeButtons = buttonSettings.value.buttons.slice(0, count);

      return activeButtons.reduce((fields, button) => {
        if (button.action === BUTTON_ACTION.FILL_DATA) {
          return fields.concat(button.textFields);
        }

        return fields;
      }, []);
    });

    const isInformationComplete = computed(() => {
      const info = programInformation.value;

      return !!(
        info.name &&
        info.description &&
        info.startDate &&
        info.endDate
      );
    });

    const areButtonsComplete = computed(() =>
      previewButtons.value.every(button => {
        if (!button.label) return false;

        if (button.action === BUTTON_ACTION.OPEN_LINK) {
          return !!button.url;
        }

        if (button.action === BUTTON_ACTION.FILL_DATA) {
          return (
            button.textFields.length > 0 &&
            button.textFields.every(field => !!field.title)
          );
        }

        return true;
      })
    );

    const isTargetComplete = computed(() => {
      if (programTarget.value.targetType !== PROGRAM_TARGET.SPECIFIC) {
        return true;
      }

      if (programTarget.value.csvFile.file) {
        return true;
      }

      return isEditMode.value && initialTargetType === PROGRAM_TARGET.SPECIFIC;
    });

    const isPublishable = computed(
      () =>
        isInformationComplete.value &&
        areButtonsComplete.value &&
        isTargetComplete.value
    );

    const isPublishModalOpen = ref(false);

    const previewProgram = computed(() => ({
      name: programInformation.value.name,
      description: programInformation.value.description,
      banner: programInformation.value.banner,
      quota: programInformation.value.quota,
      startDate: programInformation.value.startDate,
      endDate: programInformation.value.endDate,
      buttons: previewButtons.value,
      textFields: previewTextFields.value,
      targetType: programTarget.value.targetType,
    }));

    const canSaveDraft = computed(() => !!programInformation.value.name.trim());

    const isSubmitting = ref(false);
    const submitMode = ref(null);
    const isSaved = ref(false);

    const isSavingDraft = computed(
      () => isSubmitting.value && submitMode.value === PROGRAM_STATUS.DRAFT
    );
    const isPublishing = computed(
      () => isSubmitting.value && submitMode.value === PROGRAM_STATUS.PUBLISH
    );
    const isUpdating = computed(() => isSubmitting.value && isEditMode.value);

    const { $router, $toast, $error } = getCurrentInstance().proxy;

    const uploadBanner = async banner => {
      const bannerForm = new FormData();

      bannerForm.append('banner', banner);

      const { data } = await ownerProgramApi.uploadBanner(bannerForm);

      return data?.media_id ?? null;
    };

    /*
     * Caches the last successful upload so a retry after a failed publish
     * reuses the media instead of re-uploading. Keyed by File identity, so
     * changing (or removing + re-adding) the banner re-uploads.
     *
     * Seeded in edit mode with the existing banner (its URL string + media id)
     * so an untouched banner is reused on update rather than re-uploaded.
     */
    const uploadedBanner = ref({
      file: initial?.programInformation?.banner ?? null,
      mediaId: initial?.bannerMediaId ?? null,
    });

    const resolveBannerMediaId = async banner => {
      if (!banner) return null;

      if (banner === uploadedBanner.value.file) {
        return uploadedBanner.value.mediaId;
      }

      const mediaId = await uploadBanner(banner);
      uploadedBanner.value = { file: banner, mediaId };

      return mediaId;
    };

    const submitProgram = async ({ programStatus, onSuccess }) => {
      if (isSubmitting.value) return false;

      isSubmitting.value = true;
      submitMode.value = programStatus;

      try {
        const mediaId = await resolveBannerMediaId(
          programInformation.value.banner
        );

        const buildArgs = {
          programInformation: programInformation.value,
          buttonSettings: buttonSettings.value,
          programTarget: programTarget.value,
          mediaId: isEditMode.value && mediaId === null ? '' : mediaId,
          programStatus,
          removeInactiveCtas: isEditMode.value,
        };

        if (isEditMode.value) {
          await ownerProgramApi.updateOwnerProgram(
            props.programId,
            buildProgramFormData(buildArgs)
          );
        } else {
          await ownerProgramApi.createOwnerProgram(
            buildProgramFormData(buildArgs)
          );
        }

        isSaved.value = true;
        onSuccess();

        return true;
      } catch (error) {
        $error?.report?.(error);

        const fallbackMessage = isEditMode.value
          ? UPDATE_ERROR_MESSAGE
          : SUBMIT_ERROR_MESSAGE[programStatus];

        $toast.show(error?.response?.data?.error || fallbackMessage);

        return false;
      } finally {
        isSubmitting.value = false;
        submitMode.value = null;
      }
    };

    const handlePublish = () =>
      submitProgram({
        programStatus: PROGRAM_STATUS.PUBLISH,
        onSuccess: () => {
          // Today's start date goes live now; a future date is scheduled.
          const startsToday = dayjs(programInformation.value.startDate).isSame(
            dayjs(),
            'day'
          );

          isPublishModalOpen.value = false;

          $toast.show(
            startsToday
              ? 'Program berhasil di-publish.'
              : 'Program berhasil dijadwalkan.'
          );

          $router.push({ name: 'owner-program' });
        },
      });

    const saveAsDraft = () =>
      submitProgram({
        programStatus: PROGRAM_STATUS.DRAFT,
        onSuccess: () => {
          $toast.show('Draft program berhasil disimpan.');
        },
      });

    const handleSaveDraft = async () => {
      const saved = await saveAsDraft();

      if (saved) $router.push({ name: 'owner-program' });
    };

    // Exposed for the page's leave guard / beforeunload.
    const hasUnsavedChanges = computed(
      () => !isReadOnly.value && canSaveDraft.value && !isSaved.value
    );

    const submitUpdate = async programStatus => {
      const saved = await submitProgram({
        programStatus,
        onSuccess: () => {
          $toast.show(
            programStatus === PROGRAM_STATUS.DRAFT
              ? 'Draft program berhasil disimpan.'
              : UPDATE_SUCCESS_MESSAGE
          );
        },
      });

      if (saved) $router.push({ name: 'owner-program' });
    };

    // Active/scheduled/ended edit: a plain update of the existing program.
    const handleUpdate = () => submitUpdate(PROGRAM_STATUS.PUBLISH);
    const handleEndProgram = () => emit('end-program');

    const handlePrimaryAction = () => {
      if (isEditMode.value) {
        submitUpdate(PROGRAM_STATUS.PUBLISH);
      } else {
        isPublishModalOpen.value = true;
      }
    };

    const handleDraftAction = () => {
      if (isEditMode.value) {
        submitUpdate(PROGRAM_STATUS.DRAFT);
      } else {
        handleSaveDraft();
      }
    };

    return {
      isEditMode,
      isDraftStatus,
      isActiveStatus,
      isReadOnly,
      programInformation,
      buttonSettings,
      programTarget,
      previewButtons,
      previewTextFields,
      isPublishable,
      canSaveDraft,
      hasUnsavedChanges,
      isPublishModalOpen,
      isPublishing,
      isSavingDraft,
      isUpdating,
      previewProgram,
      handlePublish,
      handleSaveDraft,
      saveAsDraft,
      handleUpdate,
      handleEndProgram,
      handlePrimaryAction,
      handleDraftAction,
    };
  },
};
</script>

<style lang="scss" scoped src="./OwnerProgramForm.scss" />
